import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useState } from 'react'
import ChevronDown from '@/assets/svgs/chevron-down.svg?react'

const AccordionCard = ({
  title,
  children,
  defaultOpen = true,
  className = '',
  contentClassName = '',
  flushContent = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(!defaultOpen)

  return (
    <div
      className={clsx(
        'w-full mt-2 phone:py-6 phone:px-7 py-4 px-4 border border-grey-90 rounded-2xl bg-white',
        // Clips a flush child to the card's own radius. Without it a square-cornered
        // table paints over the border at the bottom two corners. Opt-in rather than
        // always on: the form branch holds dropdowns that are meant to escape the
        // card, and clipping those would cut their menus off.
        flushContent && 'overflow-hidden',
        className
      )}
    >
      <div
        className={clsx(
          'flex items-center gap-2 cursor-pointer phone:-mx-7 phone:px-7 -mx-4 px-4 border-b transition-[padding-bottom,border-color] duration-300 ease-in-out',
          isCollapsed ? 'pb-0 border-transparent' : 'pb-6 border-grey-90'
        )}
        onClick={() => setIsCollapsed(v => !v)}
        role="button"
        aria-expanded={!isCollapsed}
      >
        <span
          className={clsx(
            'flex items-center justify-center w-4 h-4 shrink-0 text-grey-30 transition-transform duration-300 [&_svg]:w-3 [&_svg]:h-3',
            isCollapsed ? '-rotate-90' : ''
          )}
        >
          <ChevronDown />
        </span>
        <div className="flex-1 text-sm leading-5 font-semibold text-grey-30">{title}</div>
      </div>
      <div
        className={clsx(
          'flex flex-col overflow-hidden transition-[max-height,padding-top,margin-bottom] duration-300 ease-in-out',
          // For a body that IS the card's content rather than something sitting
          // inside it — a table, whose own edges should be the card's edges. The
          // negative margins cancel the card's padding, and they go on this element
          // rather than on the child because this is the element that clips (for the
          // collapse animation), so a child's negative margin would be cut off
          // instead of shown. The title above keeps its inset either way.
          flushContent && '-mx-4 phone:-mx-7',
          // The bottom pull belongs to an open card only. Collapsed, the body has no
          // height to run to the card's edge with, so the negative margin just eats
          // the card's own bottom padding and leaves the title sitting on the bottom
          // border instead of centred between the card's edges. Transitioned with
          // the max-height above so the padding comes back over the same 300ms
          // rather than popping in at the start of the collapse.
          flushContent && !isCollapsed && '-mb-4 phone:-mb-6',
          isCollapsed ? 'max-h-0' : clsx('max-h-250', contentClassName)
        )}
      >
        {children}
      </div>
    </div>
  )
}

AccordionCard.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  defaultOpen: PropTypes.bool,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  // Lets the body run to the card's edges instead of sitting within its padding.
  flushContent: PropTypes.bool,
}

export default AccordionCard
